import { monospaceFontFamily } from "@/theme";
import { getFallbackCurrency } from "@/utils/moneyFormating";
import { NumberInput, NumberInputProps } from "@mantine/core";
import parseMoney from "parse-money";
import { useEffect, useRef, useState } from "react";

export type BigDollarInputProps = Omit<NumberInputProps, "onChange"> & {
  allowNegative?: boolean;
  onChange?: (value: number | null) => void;
  textAlign?: "left" | "center" | "right";
};

export const BigDollarInput = ({
  allowNegative = false,
  textAlign,
  ...props
}: BigDollarInputProps) => {
  const currency = getFallbackCurrency();
  const ref = useRef<null | HTMLInputElement>(null);

  const [displayValue, setDisplayValue] = useState<string | number>(
    props.value === undefined ? "" : props.value.toString(),
  );

  useEffect(() => {
    if (displayValue === 0 && props.value === "") return;

    if (
      displayValue &&
      displayValue.toString().length > 1 &&
      parseMoney(displayValue.toString())?.amount === props.value
    )
      return;

    const isDisplayValueAllZeroes = /^0+$/.test(displayValue.toString());
    if (props.value === "" && isDisplayValueAllZeroes) return;

    if (displayValue === "-" && props.value === undefined && allowNegative) {
      setDisplayValue("-");
      return;
    }

    setDisplayValue(props.value === undefined ? "" : props.value.toString());
  }, [props.value]);

  const handleChange = (value: string | number) => {
    setDisplayValue(value);
    if (!props.onChange) return;
    const isNegative = value.toString().startsWith("-");
    const parsed = parseMoney(value.toString());
    let parsedValue = parsed?.amount ?? null;

    if (
      isNegative &&
      parsedValue !== null &&
      parsedValue > 0 &&
      allowNegative
    ) {
      parsedValue *= -1;
    }

    props.onChange(parsedValue);
  };

  useEffect(() => {
    if (!ref.current) return;
    ref.current.addEventListener("keydown", (event) => {
      // cancel arrow keys
      if (["ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
    });
  }, [ref]);

  return (
    <NumberInput
      allowNegative={allowNegative}
      ref={ref}
      thousandSeparator={currency.thousandSeparator}
      decimalSeparator={currency.decimalSeparator}
      leftSection={currency.prefix}
      rightSection={currency.suffix}
      //decimalScale={2} // deciamal scale bug causes reformating when 0 is entered
      hideControls
      size="lg"
      {...props}
      value={displayValue}
      onChange={handleChange}
      styles={{
        ...props.styles,
        input: {
          textAlign: textAlign || "left",
        },
        section: {
          fontFamily: monospaceFontFamily,
        },
      }}
    />
  );
};
